import chroma from "chroma-js";
import classNames from "classnames";
import { Appearance } from "common/generator/components/button-appearance";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import type { GlideFC } from "lib/glide-fc";
import React from "react";
import { useLayer } from "react-laag";
import { useColorFromSeed } from "sharedUI/ui/views/user-preview-icon/user-preview-icon";
import tw from "twin.macro";
import { APP_MODAL_ROOT } from "webapp/lib/constants";
import { css, styled } from "webapp/styled-components";
import Img from "webapp/ui/smart/img/img";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import type { DesktopNavBarProps } from "./desktop-nav-bar";
import { UserProfileMenu } from "./desktop-nav-bar";

const Style = styled.div`
    z-index: 2;

    .menu-item:hover {
        background-color: rgba(255, 255, 255, 0.08);
    }

    .menu-item.active {
        opacity: 1;
        background-color: rgba(255, 255, 255, 0.15);
    }

    &.accent {
        background: linear-gradient(
            180deg,
            ${p => chroma(p.theme.primaryAccentColor).darken(0.15).alpha(1).css()} 0%,
            ${p => chroma(p.theme.primaryAccentColor).darken(0.65).alpha(1).css()} 100%
        );
        color: white;
        box-shadow: inset -1px 0 1px rgb(0 0 0 / 4%);
    }

    &.dark {
        background: ${p => p.theme.n700};
        color: ${p => p.theme.n0};
        box-shadow: inset -1px 0 1px rgb(0 0 0 / 12%);
    }

    &.highlight {
        background: ${p => p.theme.n0};
        color: ${p => p.theme.textDark};
        box-shadow: 1px 0 1px rgb(0 0 0 / 8%);

        .menu-item:hover {
            background-color: ${p => p.theme.n50};
        }

        .menu-item.active {
            opacity: 1;
            background-color: ${p => p.theme.n100};
        }
    }
`;

export const DesktopSideBar: GlideFC<DesktopNavBarProps> = p => {
    const {
        tabs,
        title,
        className,
        onSelectionChange,
        onSignInPressed,
        onSignUpPressed,
        onUserButtonPressed,
        onSignOutPressed,
        signedInUserEmail,
        signedInUserName,
        isUserProfileActive,
        onTitlePressed,
        logo,
    } = p;

    const username = signedInUserName ?? signedInUserEmail ?? "?";

    const colorPreview = useColorFromSeed(username);
    const background = chroma(colorPreview).brighten(3).css();

    const theme = useWireAppTheme();

    const [showUserMenu, setShowUserMenu] = React.useState(false);

    const { layerProps, triggerProps, renderLayer } = useLayer({
        isOpen: showUserMenu,
        auto: true,
        triggerOffset: 4,
        placement: "top-end",
        onOutsideClick: () => setShowUserMenu(false),
        container: APP_MODAL_ROOT,
    });

    return (
        <Style
            className={classNames(className, (theme.pageTheme ?? "accent").toLowerCase())}
            tw="flex flex-col px-3 pb-3 width[26%] max-width[270px] flex-shrink-0">
            <h1
                onClick={onTitlePressed}
                className={classNames(onTitlePressed !== undefined ? "active-title" : "")}
                css={css`
                    &.active-title {
                        ${tw`cursor-pointer`}
                    }
                `}
                tw="h-8 my-5 mb-4 text-xl font-extrabold text-center flex items-center justify-center">
                {logo !== undefined && <Img src={logo} tw="w-8 h-8 -ml-2 mr-2 object-contain" />}
                {title}
            </h1>
            <ul tw="flex-grow">
                {tabs.map(t => (
                    <li
                        onClick={() => onSelectionChange?.(t)}
                        className={classNames(t.isActive && "active", "menu-item")}
                        tw="cursor-pointer px-4 py-2 mb-px text-sm font-normal opacity-80 rounded-lg transition">
                        {t.title}
                    </li>
                ))}
            </ul>
            <div tw="flex-shrink-0 flex justify-start">
                {onSignUpPressed !== undefined && (
                    <WireButton
                        onClick={onSignUpPressed}
                        appearance={Appearance.Filled}
                        tw="flex-shrink-0 flex-grow mr-2">
                        Sign Up
                    </WireButton>
                )}
                {onSignInPressed !== undefined && (
                    <WireButton
                        onClick={onSignInPressed}
                        appearance={Appearance.Bordered}
                        tw="flex-shrink-0 flex-grow not-last:mr-2">
                        Sign In
                    </WireButton>
                )}
                {onUserButtonPressed !== undefined && (
                    <>
                        <button
                            {...triggerProps}
                            className="menu-item"
                            onClick={() => setShowUserMenu(cv => !cv)}
                            tw="flex flex-1 items-center overflow-hidden rounded-lg p-2">
                            <div
                                className={classNames(isUserProfileActive && "force-outline")}
                                style={{
                                    color: colorPreview,
                                    backgroundColor: background,
                                }}
                                css={css`
                                    &.force-outline {
                                        ${tw`ring-2 ring-white`}
                                    }
                                `}
                                tw="flex relative capitalize justify-center items-center text-base w-6 h-6
                                    flex-shrink-0 cursor-pointer rounded-full mr-2 shadow-none transform transition
                                    after:(content absolute -inset-0.5 rounded-full pointer-events-none
                                    transition-shadow) focus-visible:after:(ring-2 ring-white) hover:(shadow-lg-soft
                                    scale-105)
                                    gp-sm:(w-7 h-7)">
                                {username[0]}
                            </div>
                            <div tw="whitespace-nowrap overflow-ellipsis overflow-hidden min-w-0 flex-shrink">
                                {username}
                            </div>
                        </button>
                        {showUserMenu &&
                            renderLayer(
                                <UserProfileMenu
                                    layerProps={layerProps}
                                    onClose={() => setShowUserMenu(false)}
                                    onUserButtonPressed={onUserButtonPressed}
                                    onSignOutPressed={onSignOutPressed}
                                />
                            )}
                    </>
                )}
            </div>
        </Style>
    );
};
